import type { ActivationIssueResponse, CollectorSummary } from "@contracts/admin";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, CirclePlus, KeyRound, RadioTower, Search, ServerOff } from "lucide-react";
import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { apiRequest, jsonBody } from "@frontend/api/client";
import { ActivationCodeModal, Button, EmptyState, ErrorState, Field, LoadingState, Modal, PageHeader, StatusBadge } from "@frontend/components/ui";
import { formatRelative } from "@frontend/lib/format";
import { listCollectorStatePresentation } from "@frontend/lib/collector-state";

export function CollectorsPage() {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [activation, setActivation] = useState<ActivationIssueResponse["activation"] | null>(null);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["collectors"],
    queryFn: () => apiRequest<{ data: CollectorSummary[] }>("/api/v1/admin/collectors?limit=200"),
    refetchInterval: 30_000,
  });
  const create = useMutation({
    mutationFn: (name: string) => apiRequest<ActivationIssueResponse>("/api/v1/admin/collectors", {
      method: "POST",
      body: jsonBody({ name }),
    }),
    onSuccess: async (result) => {
      setCreateOpen(false);
      setActivation(result.activation);
      await queryClient.invalidateQueries({ queryKey: ["collectors"] });
      await queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });

  const collectors = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase("pt-BR");
    return (query.data?.data ?? []).filter((collector) =>
      !normalized || [collector.name, collector.hostname, collector.collector_version]
        .some((value) => value?.toLocaleLowerCase("pt-BR").includes(normalized)),
    );
  }, [query.data, search]);

  return (
    <>
      <PageHeader
        title="Coletores"
        actions={<Button onClick={() => setCreateOpen(true)}><CirclePlus size={17} /> Novo coletor</Button>}
      />

      <div className="toolbar">
        <label className="search-input"><Search size={17} /><input aria-label="Buscar coletores" onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por nome, host ou versão" value={search} /></label>
        <span aria-live="polite">{collectors.length} {collectors.length === 1 ? "coletor" : "coletores"}</span>
      </div>

      {query.isLoading ? <LoadingState label="Carregando coletores" /> : null}
      {query.isError ? <ErrorState message="A lista de coletores não respondeu." retry={() => void query.refetch()} /> : null}
      {!query.isLoading && !query.isError && collectors.length === 0 ? (
        <EmptyState title={search ? "Nenhum resultado" : "Nenhum coletor"} action={!search ? <Button onClick={() => setCreateOpen(true)}>Criar coletor</Button> : undefined} />
      ) : null}

      {collectors.length > 0 ? (
        <div className="data-table-wrap">
          <table className="data-table collectors-table">
            <caption className="sr-only">Coletores cadastrados</caption>
            <thead><tr><th>Coletor</th><th>Estado</th><th>Câmeras</th><th>Fila</th><th>Versão</th><th>Último contato</th><th aria-label="Abrir" /></tr></thead>
            <tbody>{collectors.map((collector) => {
              const state = listCollectorStatePresentation(collector);
              return (
                <tr key={collector.id}>
                  <td data-label="Coletor"><div className="entity-cell"><span className={`entity-icon ${state.operational ? "entity-icon--online" : ""}`}>{collector.activation_status === "revoked" ? <ServerOff size={18} /> : collector.activation_status === "pending" ? <KeyRound size={18} /> : <RadioTower size={18} />}</span><div><Link to={`/collectors/${collector.id}`}>{collector.name}</Link><small>{collector.hostname ?? (collector.activation_status === "pending" ? "Aguardando instalação" : "Sem telemetria")}</small></div></div></td>
                  <td data-label="Estado"><StatusBadge tone={state.tone}>{state.label}</StatusBadge></td>
                  <td data-label="Câmeras"><strong>{collector.camera_count}</strong></td>
                  <td data-label="Fila"><strong className={(collector.upload_queue?.pendentes ?? 0) > 0 ? "text-warning" : ""}>{collector.upload_queue?.pendentes ?? 0}</strong></td>
                  <td data-label="Versão"><span className="mono-small">{collector.collector_version ?? "—"}</span></td>
                  <td data-label="Último contato"><span title={collector.last_seen_at ?? ""}>{formatRelative(collector.last_seen_at)}</span></td>
                  <td><Link aria-label={`Abrir ${collector.name}`} className="row-action" to={`/collectors/${collector.id}`}><ArrowRight size={17} /></Link></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      ) : null}

      <CreateCollectorModal busy={create.isPending} error={create.error instanceof Error ? create.error.message : null} key={createOpen ? "create-open" : "create-closed"} open={createOpen} onClose={() => setCreateOpen(false)} onSubmit={(name) => create.mutate(name)} />
      <ActivationCodeModal open={activation !== null} activation={activation} onClose={() => setActivation(null)} />
    </>
  );
}

function CreateCollectorModal({ open, busy, error, onClose, onSubmit }: { open: boolean; busy: boolean; error: string | null; onClose: () => void; onSubmit: (name: string) => void }) {
  const [name, setName] = useState("");
  function submit(event: FormEvent) { event.preventDefault(); onSubmit(name); }
  return <Modal open={open} title="Novo coletor" description="O código de ativação será exibido uma vez." onClose={onClose}><form className="form-stack" onSubmit={submit}><Field label="Nome da instalação"><input autoFocus maxLength={160} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Viena — Sete Lagoas" required value={name} /></Field>{error ? <div className="form-alert">{error}</div> : null}<div className="form-actions"><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button disabled={busy} type="submit">{busy ? "Criando…" : "Criar coletor"}</Button></div></form></Modal>;
}
